import React, { useState } from "react";
import { Plus, Trash2, MapPin, ArrowUp, ArrowDown, ChevronDown } from "lucide-react";
import { LocationPicker } from "@/components/LocationPicker";

const fld = "w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none";

function StopRow({ s, i, total, withTime, prefix, onChange, onDelete, onMove }) {
  const [open, setOpen] = useState(false);
  const set = (k, v) => onChange({ ...s, [k]: v });
  return (
    <div data-testid={`${prefix}-stop-${i}`} className="rounded-xl border border-slate-200 p-2.5 space-y-2 bg-white">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
        {withTime && <input type="time" data-testid={`${prefix}-stop-time-${i}`} value={s.time || ""} onChange={(e) => set("time", e.target.value)} className="w-28 bg-slate-50 border border-slate-200 rounded-xl h-10 px-2 text-sm" />}
        <div className="flex-1" />
        <button type="button" disabled={i === 0} onClick={() => onMove(-1)} className="p-1.5 text-slate-500 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
        <button type="button" disabled={i === total - 1} onClick={() => onMove(1)} className="p-1.5 text-slate-500 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
        <button type="button" data-testid={`${prefix}-stop-del-${i}`} onClick={onDelete} className="p-1.5 text-red-500"><Trash2 className="w-4 h-4" /></button>
      </div>
      <input data-testid={`${prefix}-stop-name-${i}`} value={s.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Emri i ndalesës (p.sh. Göteborg)" className={`${fld} font-semibold`} />
      <input data-testid={`${prefix}-stop-address-${i}`} value={s.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Adresa (p.sh. Nils Ericson Terminalen)" className={fld} />
      <button type="button" data-testid={`${prefix}-stop-map-${i}`} onClick={() => setOpen(!open)} className={`w-full flex items-center justify-center gap-1.5 h-10 rounded-xl text-sm font-semibold border ${s.lat != null ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-600"}`}>
        <MapPin className="w-4 h-4" />{s.lat != null ? "Vendndodhja u gjet ✓" : "Gjej në hartë"}<ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <LocationPicker coords={s.lat != null ? { lat: s.lat, lng: s.lng } : null}
          setCoords={(c) => onChange({ ...s, lat: c.lat, lng: c.lng })}
          country="" city={s.name} address={s.address} testid={`${prefix}-stop-loc-${i}`} />
      )}
    </div>
  );
}

export function StopsEditor({ stops = [], onChange, withTime = true, prefix = "stops" }) {
  const move = (i, d) => { const a = [...stops]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
  return (
    <div className="mt-3" data-testid={`${prefix}-editor`}>
      <div className="text-sm font-semibold text-slate-700 mb-1">Pikat e ndalesës</div>
      <p className="text-xs text-slate-400 mb-2">E para është nisja, e fundit destinacioni. Çdo ndalesë del si shenjë në hartë.</p>
      <div className="space-y-2">
        {stops.map((s, i) => (
          <StopRow key={i} s={s} i={i} total={stops.length} withTime={withTime} prefix={prefix}
            onChange={(ns) => onChange(stops.map((x, k) => (k === i ? ns : x)))}
            onDelete={() => onChange(stops.filter((_, k) => k !== i))} onMove={(d) => move(i, d)} />
        ))}
      </div>
      <button type="button" data-testid={`${prefix}-stop-add`} onClick={() => onChange([...stops, { time: "", name: "", address: "", lat: null, lng: null }])}
        className="mt-2 w-full h-11 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 text-sm font-semibold flex items-center justify-center gap-1.5">
        <Plus className="w-4 h-4" />Shto ndalesë
      </button>
    </div>
  );
}

export function StopsTimeline({ stops = [], icon: Icon, tid = "stops-timeline" }) {
  const list = stops.filter((s) => s.name?.trim());
  if (!list.length) return null;
  const withTime = list.some((s) => s.time);
  return (
    <section className="px-4 mt-5" data-testid={tid}>
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900 mb-3">{Icon && <Icon className="w-6 h-6 text-emerald-500" />}Pikat e ndalesës</h2>
      <div>
        {list.map((s, i) => {
          const end = i === 0 || i === list.length - 1;
          return (
            <div key={i} className="flex items-stretch gap-3" data-testid={`${tid}-stop-${i}`}>
              <div className="relative w-5 flex justify-center shrink-0">
                {i > 0 && <span className="absolute top-0 h-1/2 w-0.5 bg-emerald-500" />}
                {i < list.length - 1 && <span className="absolute bottom-0 h-1/2 w-0.5 bg-emerald-500" />}
                <span className={`relative z-10 self-center rounded-full ${end ? "w-5 h-5 bg-emerald-500" : "w-3.5 h-3.5 bg-white border-2 border-emerald-500"}`} />
              </div>
              {withTime && <div className="self-center shrink-0 w-16 text-center bg-slate-100 rounded-lg py-1.5 text-sm font-semibold text-slate-800">{s.time || "—"}</div>}
              <div className="flex-1 min-w-0 py-2 border-b border-slate-100">
                <div className="text-base font-semibold text-slate-900 leading-tight break-words">{s.name}</div>
                {s.address && <div className="text-xs text-slate-500 break-words">{s.address}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
